import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { balanceAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft,
  Euro,
  TrendingUp,
  TrendingDown,
  History
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

export default function CustomerPayments() {
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [historyRes, balanceRes] = await Promise.all([
        balanceAPI.getHistory(),
        balanceAPI.get(),
      ]);
      setBalanceHistory(historyRes.data);
      setBalance(balanceRes.data.balance);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Zahlungshistorie');
    } finally {
      setLoading(false);
    }
  };

  const getBalanceColor = () => {
    if (balance < 0) return 'text-red-600';
    if (balance > 0) return 'text-emerald-600';
    return 'text-gray-900';
  };

  const getBalanceLabel = () => {
    if (balance < 0) return 'Offener Betrag';
    if (balance > 0) return 'Guthaben';
    return 'Ausgeglichen';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 md:py-8" data-testid="customer-payments">
      <div className="max-w-4xl mx-auto px-4 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="px-2 md:px-4">
              <ArrowLeft className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Zurück</span>
            </Button>
          </Link>
          <h1 className="font-barlow font-bold text-xl md:text-2xl text-[#1A1A1A]">
            Zahlungen & Guthaben
          </h1>
        </div>

        {/* Balance Card */}
        <Card className="mb-6 md:mb-8" data-testid="balance-card">
          <CardContent className="p-4 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="font-manrope text-sm text-gray-500 mb-1">Aktueller Kontostand</p>
                <p className={`font-barlow font-bold text-4xl ${getBalanceColor()}`}>
                  {balance.toFixed(2).replace('.', ',')} €
                </p>
                <p className="font-manrope text-sm text-gray-500 mt-1">{getBalanceLabel()}</p>
              </div>
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                balance < 0 ? 'bg-red-100' : balance > 0 ? 'bg-emerald-100' : 'bg-gray-100'
              }`}>
                {balance < 0 ? (
                  <TrendingDown className="w-10 h-10 text-red-600" />
                ) : (
                  <TrendingUp className="w-10 h-10 text-emerald-600" />
                )}
              </div>
            </div>
            {balance < 0 && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg">
                <p className="font-manrope text-sm text-red-700">
                  Sie haben einen offenen Betrag von <strong>{Math.abs(balance).toFixed(2).replace('.', ',')} €</strong>.
                  Bitte überweisen Sie den Betrag auf unser Konto oder kontaktieren Sie uns.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance History List */}
        <Card data-testid="balance-history-list">
          <CardHeader>
            <CardTitle className="font-barlow text-xl flex items-center gap-2">
              <History className="w-5 h-5 text-[#9B1B30]" />
              Zahlungshistorie ({balanceHistory.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {balanceHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 font-barlow font-bold text-sm text-gray-500">Datum & Uhrzeit</th>
                      <th className="text-left py-3 font-barlow font-bold text-sm text-gray-500">Beschreibung</th>
                      <th className="text-right py-3 font-barlow font-bold text-sm text-gray-500">Betrag</th>
                      <th className="text-right py-3 font-barlow font-bold text-sm text-gray-500">Neuer Stand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceHistory.map((entry) => (
                      <tr 
                        key={entry.id} 
                        className="border-b last:border-0 hover:bg-gray-50 transition-colors"
                        data-testid={`history-${entry.id}`}
                      >
                        <td className="py-4 font-manrope text-sm">
                          {format(new Date(entry.updated_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </td>
                        <td className="py-4 font-manrope text-sm">{entry.description}</td>
                        <td className={`py-4 font-barlow font-bold text-right ${
                          entry.amount < 0 ? 'text-red-600' : 'text-emerald-600'
                        }`}>
                          <div className="flex items-center justify-end gap-1">
                            {entry.amount < 0 ? (
                              <TrendingDown className="w-4 h-4" />
                            ) : (
                              <TrendingUp className="w-4 h-4" />
                            )}
                            {entry.amount > 0 ? '+' : ''}{entry.amount.toFixed(2).replace('.', ',')} €
                          </div>
                        </td>
                        <td className="py-4 font-barlow font-bold text-right text-gray-600">
                          {entry.new_balance.toFixed(2).replace('.', ',')} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Euro className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-manrope text-gray-500 mb-2">Keine Zahlungen vorhanden</p>
                <p className="font-manrope text-sm text-gray-400">
                  Hier wird Ihre Zahlungshistorie angezeigt, sobald Buchungen vorgenommen werden.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
